import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  BRANDING_WHITE,
  darkModeHoverHex,
  darkenHex,
  hexToRgb,
  primaryFontFamily,
  readableForeground,
  sanitizeCssColor,
  sanitizeFontFamily,
  sanitizeHttpUrl,
  type Rgb,
} from '@airclou/utils';
import { getBrandConfig, getSession, signOut as apiSignOut } from '@/services/api';
import type { BrandConfig, PortalViewer } from '@/types';

interface BrandContextValue {
  brand: BrandConfig | null;
  isLoading: boolean;
  error: Error | null;
  viewer: PortalViewer | null;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refetchSession: () => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

// Colour handling.
//
// Every colour below goes through ONE parser — `hexToRgb` from
// `@airclou/utils`, the same function the helpdesk write route validates with
// and the chat widget derives its colours with. Previously `--primary` used a
// local 6-digit, hash-optional regex with a navy fallback while
// `--primary-foreground` used the shared rule, so `#fff` produced dark text on
// a navy button and a hash-less `f5f5dc` produced white text on beige: two
// parsers with two ideas of "invalid" fell back separately. Now a colour
// either parses and drives all its derived variables, or it is skipped as a
// whole and the portal's own CSS defaults stay — a substituted value would
// be a silent lie about what the brand configured.
//
// The write route refuses anything that is not hex and the columns carry a
// matching CHECK (2026-09-06), so a stored colour is one `hexToRgb` accepts.
// `sanitizeCssColor` still runs first: it is the same normaliser the route
// applies, so a value that reaches here by any other path is read the same
// way the route would have written it.

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
        break;
      case gn:
        h = ((bn - rn) / d + 2) / 6;
        break;
      case bn:
        h = ((rn - gn) / d + 4) / 6;
        break;
    }
  }
  return { h, s, l };
}

/** `"h s% l%"` — the form the portal's HSL-triplet CSS variables expect. */
function hslTriplet({ h, s, l }: { h: number; s: number; l: number }): string {
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Hover shades, one per theme. Light mode uses the shared `darkenHex`, the
 * same rule the chat widget's `--hdw-primary-hover` uses, so a brand's buttons
 * hover to one shade on both surfaces of one customer site (the widget is
 * always light). A local rule used to lighten dark colours instead, which put
 * a lighter portal button next to a darker widget button for the same brand
 * blue. Dark mode lightens instead — darkening there moved a hovered button
 * toward the page, so it looked less prominent under the pointer — but only
 * as far as the label stays readable (`darkModeHoverHex`, 2026-09-29).
 * Callers only reach this with a colour `hexToRgb` accepted, so the fallback
 * to the rest colour is unreachable in practice.
 */
function hoverTriplets(hex: string): { light: string; dark: string } {
  const toTriplet = (c: string) => hslTriplet(rgbToHsl(hexToRgb(c)!));
  return {
    light: toTriplet(darkenHex(hex) ?? hex),
    dark: toTriplet(darkModeHoverHex(hex, getContrastColor(hex)) ?? hex),
  };
}

// Contrasting foreground for a brand colour, as a hex string.
//
// Uses the shared WCAG-luminance rule from `@airclou/utils` rather than a local
// perceived-brightness formula: the helpdesk chat widget derives its header
// text from the same brand colour with that rule, and the two surfaces sit
// side by side on one customer site. A local formula disagreed with it on
// roughly one in nine colours — white text in the portal header, dark ink in
// the widget header, for the same brand red. Callers only reach this with a
// colour `hexToRgb` already accepted, so the white fallback is unreachable in
// practice; it is there so the return type is honest.
function getContrastColor(hex: string): string {
  return readableForeground(hex) ?? BRANDING_WHITE;
}

function applyBrandTheme(brand: BrandConfig) {
  const root = document.documentElement;

  // Primary color — all five variables derive from one parse, or none do.
  // Normalised first, like the header and hero colours below: `hexToRgb`
  // wants the hash, and a hash-less row silently lost all its variables while
  // the header three lines down kept its colour.
  const primaryHex = sanitizeCssColor(brand.primary_color);
  const primary = primaryHex ? hexToRgb(primaryHex) : null;
  if (primary && primaryHex) {
    const triplet = hslTriplet(rgbToHsl(primary));
    root.style.setProperty('--primary', triplet);
    const hover = hoverTriplets(primaryHex);
    root.style.setProperty('--primary-hover-light', hover.light);
    root.style.setProperty('--primary-hover-dark', hover.dark);
    root.style.setProperty(
      '--primary-foreground',
      hslTriplet(rgbToHsl(hexToRgb(getContrastColor(primaryHex))!)),
    );
    root.style.setProperty('--ring', triplet);
  }

  // Header colors
  // If a brand sets a header background, treat it as the *light* theme value.
  // Dark theme can either use an explicit dark override (future) or fall back to CSS defaults.
  const headerBg = sanitizeCssColor(brand.header_bg_color);
  const headerText = sanitizeCssColor(brand.header_text_color);
  if (headerBg) {
    root.style.setProperty('--header-bg-light', headerBg);
    root.style.removeProperty('--header-bg');
  }
  if (headerText) {
    root.style.setProperty('--header-text-light', headerText);
    root.style.removeProperty('--header-text');
  } else if (headerBg) {
    root.style.setProperty('--header-text-light', getContrastColor(headerBg));
    root.style.removeProperty('--header-text');
  }

  // Hero color
  const heroBg = sanitizeCssColor(brand.hero_bg_color);
  if (heroBg) {
    root.style.setProperty('--hero-bg-light', heroBg);
    root.style.removeProperty('--hero-bg');
  }

  // Font family — same allowlist the write route applies. The whole stack is
  // the CSS value; only its FIRST family is the web-font request. Encoding
  // the entire stack into one `family=` asked Google Fonts for a font called
  // `"Segoe UI", Arial, sans-serif`, which is a 400 on every pageview and no
  // font. A stack that leads with a generic family, or with one of the common
  // system fonts (`Segoe UI`, `Arial`, …), requests nothing: those are
  // installed already, and Google Fonts answers 400 for them too. A less
  // common system font still costs one failed request — a console line, not
  // a wrong font, since the link is script-inserted and never render-blocking.
  const fontFamily = sanitizeFontFamily(brand.font_family);
  if (fontFamily) {
    root.style.setProperty('--font-family', fontFamily);
    document.getElementById('brand-font')?.remove();
    const webFont = primaryFontFamily(fontFamily);
    if (webFont) {
      const link = document.createElement('link');
      link.id = 'brand-font';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(webFont)}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  }

  // Favicon — sanitised like everything else this function applies. The write
  // route requires http(s), so this only ever differs for a row written before
  // it did; a stored `javascript:` here is not executable (browsers never run a
  // `link[rel=icon]` href), but leaving one value in the function outside the
  // one-parser rule stated above is how the next one gets missed.
  const faviconUrl = sanitizeHttpUrl(brand.favicon_url);
  if (faviconUrl) {
    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = faviconUrl;
  }

  // Page title
  document.title = `${brand.name} Help Center`;
}

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [viewer, setViewer] = useState<PortalViewer | null>(null);

  const {
    data: brand,
    isLoading: brandLoading,
    error: brandError,
  } = useQuery({
    queryKey: ['brand'],
    queryFn: getBrandConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on brand not found or disabled errors
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 404 || status === 503) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });

  const {
    data: session,
    refetch: refetchSession,
  } = useQuery({
    queryKey: ['session'],
    queryFn: getSession,
    staleTime: 60 * 1000, // 1 minute
    retry: false,
  });

  useEffect(() => {
    if (session) {
      setViewer(session);
    }
  }, [session]);

  useEffect(() => {
    if (brand) {
      applyBrandTheme(brand);
    }
  }, [brand]);

  const signOut = async () => {
    await apiSignOut();
    setViewer(null);
    refetchSession();
  };

  const value = useMemo(
    () => ({
      brand: brand ?? null,
      isLoading: brandLoading,
      error: brandError as Error | null,
      viewer,
      isAuthenticated: !!viewer,
      signOut,
      refetchSession: () => refetchSession(),
    }),
    [brand, brandLoading, brandError, viewer, refetchSession]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
